import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { connect } from 'react-redux'
import { compose } from 'recompose'
import { Link } from 'react-router-dom'

import { signOut } from 'data/auth/actions'

import PicInput from 'components/Utils/PicInput'
import UpdateEmailForm from './UpdateEmailForm'
import { Select, Text } from 'components/base'
import { Input } from 'components/Utils/Form'
import {
  createSuccessToasterMessage,
  createErrorToasterMessage,
} from 'data/ui/actions'
import { providesUserPreferencesMutations } from './dataSync'
import { providesUsersSubscriptions } from 'components/Users/dataSync'
import { Button } from 'components/base'
import RoutesHelper from 'helpers/RoutesHelper'
import { isOwner } from 'data/users/record'
import history from 'browserHistory'

import UserRemoveAccountModal from './removalModal'
import SaveBanner from '../SaveBanner'

import {
  record as UsersRecord,
  actions as UsersActions,
  selectors as UsersSelectors,
} from 'data/users'
import {
  record as OrganizationsRecord,
  selectors as OrganizationsSelectors,
} from 'data/organizations'
import {
  record as UserPreferencesRecord,
  selectors as UserPreferencesSelectors,
} from 'data/userPreferences'

const DIGEST_FREQUENCIES = [
  {
    title: 'Never',
    description: `Don't send emails`,
    value: 'never',
  },
  {
    title: 'Immediately',
    description: 'Send emails about as soon as you receive the notification',
    value: 'immediately',
  },
  {
    title: 'Hourly',
    description: 'Send emails about every hour',
    value: 'hourly',
  },
  {
    title: 'Daily',
    description: 'Send emails about once a day',
    value: 'daily',
  },
  {
    title: 'Weekly',
    description: 'Send emails about once a week',
    value: 'weekly',
  },
]

const SLITE_LINKS_OPTIONS = [
  {
    title: 'Open in browser',
    description: `Slite link will open in your default browser`,
    value: false,
  },
  {
    title: 'Open in desktop app',
    description: 'We will attempt to open links in the app if it installed',
    value: true,
  },
]

class SettingsProfile extends Component {
  static propTypes = {
    viewer: PropTypes.instanceOf(UsersRecord).isRequired,
    organization: PropTypes.instanceOf(OrganizationsRecord).isRequired,
    updateUser: PropTypes.func.isRequired,
    updateUserPhoto: PropTypes.func.isRequired,
    userPreferences: PropTypes.instanceOf(UserPreferencesRecord).isRequired,
    updateUserPreferences: PropTypes.func.isRequired,
  }
  static defaultProps = {
    viewer: {},
  }

  constructor(props) {
    super(props)
    this.state = this.initialState
  }

  initialState = {
    username: this.props.viewer.username || '',
    displayName: this.props.viewer.displayName || '',
    photoURL: this.props.viewer.photoURL || '',
    notificationsDigestFrequency: this.props.userPreferences
      .notificationsDigestFrequency,
    isDesktopUser: this.props.userPreferences.isDesktopUser || false,
  }

  componentWillReceiveProps({ viewer: { photoURL } }) {
    if (photoURL !== this.props.viewer.photoURL) {
      this.setState({ photoURL })
    }
  }

  onPhotoUpload = photoFile => {
    this.props.updateUserPhoto({ photoFile })
  }

  onFormValueChange = event => {
    const { name, value } = event.target
    this.setState(state => ({
      [name]: name === 'username' ? this.formatUsername(value) : value,
    }))
  }

  formatUsername = username =>
    username.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()

  onValidate = event => {
    event.preventDefault()

    const { updateUser, updateUserPreferences, userPreferences } = this.props

    // Update user information if needed
    const fieldsThatChanged = this.getChangedFields()

    const newUserAttributes = fieldsThatChanged.reduce(
      (attrs, fieldName) => ({
        ...attrs,
        [fieldName]: this.state[fieldName],
      }),
      {}
    )
    updateUser(newUserAttributes)

    // Update user preferences if needed
    const { notificationsDigestFrequency, isDesktopUser } = this.state
    if (
      notificationsDigestFrequency !==
        userPreferences.notificationsDigestFrequency ||
      isDesktopUser !== Boolean(userPreferences.isDesktopUser)
    ) {
      updateUserPreferences({ notificationsDigestFrequency, isDesktopUser })
    }
    history.replace(RoutesHelper.home())
  }

  getChangedFields = () => {
    return ['photoURL', 'username', 'displayName'].filter(
      fieldName => this.props.viewer[fieldName] !== this.state[fieldName]
    )
  }

  canSave = () =>
    Boolean(
      this.getChangedFields().length ||
        this.state.notificationsDigestFrequency !==
          this.props.userPreferences.notificationsDigestFrequency ||
        this.state.isDesktopUser !==
          Boolean(this.props.userPreferences.isDesktopUser)
    )

  renderUserRemoval() {
    return (
      <React.Fragment>
        <div className="margin-top-huge margin-bottom-normal padding-top-huge border-top-small border-shade-40">
          <Text.Subtitle>Account deletion</Text.Subtitle>
        </div>
        {isOwner(this.props.viewer) ? (
          <Text.Corpus>
            You are currently the owner of this team, please transfer ownership
            before deleting your account. You can do this from{' '}
            <span
              className="underline pointer shade-20 shade-05-on-hover font-weight-medium"
              onClick={() => history.push(RoutesHelper.settingsUsers())}
            >
              Member Management.
            </span>
            <div className="margin-top-small">
              If you want to delete{' '}
              <span className="bold">your entire Slite team</span>, including
              your personal account, you can do this from{' '}
              <span
                className="underline pointer shade-20 shade-05-on-hover font-weight-medium"
                onClick={() =>
                  history.push(RoutesHelper.settingsOrganizationIndex())
                }
              >
                Team settings.
              </span>{' '}
              Please note that this action cannot be undone.
            </div>
          </Text.Corpus>
        ) : (
          <div className="margin-bottom-huge">
            <Text.Detail className="font-size-large line-height-larger">
              By deleting your personal account, you delete all associated data,
              excluding content you've shared with your team. Think before
              acting, there's no turning back!
            </Text.Detail>
            <UserRemoveAccountModal onRemove={this.handleUserRemoval}>
              <Button.Danger className="margin-top-large">
                Confirm deletion
              </Button.Danger>
            </UserRemoveAccountModal>
          </div>
        )}
      </React.Fragment>
    )
  }

  onClickSupport = event => {
    if (window.Intercom) {
      event.preventDefault()
      window.Intercom('show')
    }
  }

  handleUserRemoval = ({ success }) => {
    if (success) {
      return this.props.signOut()
    }

    this.props.createErrorToasterMessage({
      title: 'An error occured while closing your account.',
      detail:
        'Please try again or reach out to the support to solve this issue.',
    })
  }

  onCancel = () => this.setState(this.initialState)

  render() {
    const { viewer, updatePhotoState } = this.props

    const {
      username,
      displayName,
      notificationsDigestFrequency,
      isDesktopUser,
    } = this.state

    return (
      <React.Fragment>
        <div className="padding-vertical-large" style={{ width: 640 }}>
          <Text.Name className="margin-bottom-normal shade-05">Photo</Text.Name>
          <PicInput
            src={this.state.photoURL}
            title={viewer.username}
            onChange={this.onPhotoUpload}
            help="Must be at least 300px by 300px"
            updateState={updatePhotoState}
          />
          <div className="padding-top-huge margin-bottom-small">
            <Text.Subtitle className="margin-bottom-large">
              Personal info
            </Text.Subtitle>
            <div className="flexbox margin-top-normal margin-bottom-large flexbox-wrap">
              <div className="margin-right-small flexbox-grow">
                <div
                  className="flexbox flexbox-align-center margin-bottom-normal"
                  style={{ minHeight: '20px' }}
                >
                  <div className="margin-right-auto">
                    <Text.Name>Username</Text.Name>
                  </div>
                </div>
                <Input
                  id="username"
                  name="username"
                  placeholder="neil"
                  autoComplete="off"
                  className="padding-none width-full"
                  value={username}
                  onChange={this.onFormValueChange}
                />
              </div>
              <div className="margin-left-small flexbox-grow">
                <div
                  className="flexbox flexbox-align-center margin-bottom-normal"
                  style={{ minHeight: 20 }}
                >
                  <div className="margin-right-auto">
                    <Text.Name>Display name</Text.Name>
                  </div>
                </div>
                <Input
                  name="displayName"
                  id="displayName"
                  placeholder="Neil Armstrong"
                  autoComplete="off"
                  className="padding-none width-full"
                  value={displayName}
                  onChange={this.onFormValueChange}
                />
              </div>
            </div>
            <div className="flexbox flexbox-wrap">
              <UpdateEmailForm />
            </div>
          </div>

          <div className="padding-top-largest margin-bottom-small">
            <Text.Subtitle className="margin-bottom-medium">
              Password
            </Text.Subtitle>
            <Link to={RoutesHelper.requestResetPassword()}>
              <Button.Secondary>Change your password</Button.Secondary>
            </Link>
          </div>

          <div className="padding-top-largest margin-bottom-small">
            <Text.Subtitle className="margin-bottom-medium">
              Preferences
            </Text.Subtitle>
            <div className="margin-vertical-large">
              <Text.Name className="margin-bottom-normal">
                Notifications digest frequency
              </Text.Name>
              <Select
                options={DIGEST_FREQUENCIES}
                onChange={value =>
                  this.setState({ notificationsDigestFrequency: value })
                }
                value={notificationsDigestFrequency}
              />
              <React.Fragment>
                <Text.Name className="margin-bottom-normal margin-top-large">
                  Where to open Slite links?
                </Text.Name>
                <Select
                  options={SLITE_LINKS_OPTIONS}
                  onChange={value => this.setState({ isDesktopUser: value })}
                  value={isDesktopUser}
                />
              </React.Fragment>
            </div>
          </div>
          {this.renderUserRemoval()}
        </div>
        {this.canSave() && (
          <SaveBanner onCancel={this.onCancel} onSave={this.onValidate} />
        )}
      </React.Fragment>
    )
  }
}

const mapStateToProps = state => {
  const viewer = UsersSelectors.getCurrentUser(state)
  const organization = OrganizationsSelectors.getOrganization(
    state,
    viewer.organizationId
  )
  const userPreferences = UserPreferencesSelectors.getUserPreferences(state)

  return {
    viewer,
    organization,
    updatePhotoState: UsersSelectors.getUpdatePhotoState(state),
    userPreferences,
  }
}

const mapDispatchToProps = {
  ...UsersActions,
  createSuccessToasterMessage,
  createErrorToasterMessage,
  signOut,
}

export default compose(
  providesUserPreferencesMutations,
  providesUsersSubscriptions,
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(SettingsProfile)
